import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Button from "../../components/ui/Button";
import ProductCard from "./components/ProductCard";
import { categoriesApi, subcategoriesApi, productsApi } from "../../api";
import SaleHero from "./components/SaleHero";
import UrgencyBanner from "./components/UrgencyBanner";
import PaginationControls from "./components/PaginationControls";
import CombinedFilter from "./components/CombinedFilter";
import { useDebounce } from "../../hooks/useDebounce";
import { useDialog } from "../../contexts/DialogContext";
import Dialog from "../../components/ui/Dialog";
import Image from "../../components/AppImage";
import Icon from "../../components/AppIcon";
import { useCart } from "../../contexts/CartContext";
import { toast } from "react-hot-toast";
import { useTheme } from "../../contexts/ThemeContext";

const Collections = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const MAX_PRICE = 1000; // Or a more appropriate max value
  const ITEMS_PER_PAGE = 16;

  // State for filter data and active filters
  const [filterData, setFilterData] = useState([]);
  const [activeFilters, setActiveFilters] = useState({ categories: [], subcategories: [] });
  const [priceRange, setPriceRange] = useState({ min: 0, max: MAX_PRICE });
  const debouncedPriceRange = useDebounce(priceRange, 500);

  // State for products
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [paginatedProducts, setPaginatedProducts] = useState([]);
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Loading states
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [addingProductId, setAddingProductId] = useState(null);

  // State for the dialog
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDialogSize, setSelectedDialogSize] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const { isDialogOpen, setIsDialogOpen } = useDialog();
  const { addToCart, cartItems } = useCart();

  // Update images array when selectedProduct changes
  useEffect(() => {
    if (selectedProduct) {
      const productImages = [
        selectedProduct.image,
        selectedProduct.urlImageHover
      ].filter(Boolean);
      setImages(productImages);
      setCurrentImageIndex(0);
    }
  }, [selectedProduct]);

  // Navigation functions for image slider
  const handleNextImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrevImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  // Vérifier si un produit est déjà dans le panier avec une comparaison plus précise
  const isInCart = (productId, size) => {
    return cartItems.some(item => 
      item.id === productId && 
      (!size || item.size === size || size === 'UNIQUE' || item.size === 'UNIQUE')
    );
  };

  const handleProductClick = (product) => {
    const isSale = product.sale && product.sale.isActive;
    const displayPrice = isSale ? parseFloat(product.sale.discountedPrice) : parseFloat(product.price);
    const originalPrice = isSale ? parseFloat(product.price) : null;

    const formattedProduct = {
        ...product, // Pass original product data
        id: product.id,
        title: product.name,
        description: product.description || "",
        price: displayPrice,
        originalPrice: originalPrice,
        image: product.urlImage,
        category: product.category?.name || "",
        inStock: product.stock > 0,
        stockCount: product.stock || 0,
        availableSizes: product.size ? product.size.split(',').map(s => s.trim()) : []
    };
    setSelectedProduct(formattedProduct);
    // Sélectionner la première taille disponible par défaut
    const firstSize = formattedProduct.availableSizes?.[0] || 'UNIQUE';
    setSelectedDialogSize(firstSize);
    setIsDialogOpen(true);
  };

  // Fonction utilitaire pour afficher la notification "Déjà dans le panier"
  const showAlreadyInCartToast = () => {
    toast.dismiss(); // Fermer les notifications précédentes
    toast((t) => (
      <div className="flex items-center space-x-3">
        <div className="bg-white p-1 rounded-full">
          <Icon name="ShoppingBag" className="w-6 h-6 text-blue-500" />
        </div>
        <div>
          <p className="font-semibold text-white">Déjà dans le panier</p>
          <p className="text-sm text-white/90">Ce produit est déjà dans votre panier</p>
        </div>
      </div>
    ), {
      position: 'bottom-center',
      duration: 2000,
      style: {
        background: '#3B82F6',
        color: '#fff',
        padding: '16px 24px',
        borderRadius: '12px',
        maxWidth: '90%',
        width: 'auto',
        margin: '0 auto 20px',
        zIndex: 9999
      },
      className: 'toast-notification',
      icon: '🛒'
    });
  };

  // Fonction utilitaire pour afficher la notification de succès
  const showSuccessToast = () => {
    toast((t) => (
      <div className="flex items-center space-x-3">
        <div className="bg-white p-1 rounded-full">
          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <div>
          <p className="font-semibold text-white">Produit ajouté !</p>
          <p className="text-sm text-white/90">Votre article a été ajouté au panier</p>
        </div>
      </div>
    ), {
      position: 'bottom-center',
      duration: 3000,
      style: {
        background: '#10B981',
        color: '#fff',
        padding: '16px 24px',
        borderRadius: '12px',
        maxWidth: '90%',
        width: 'auto',
        margin: '0 auto 20px',
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      },
      className: 'animate-bounce',
      iconTheme: {
        primary: '#fff',
        secondary: '#10B981'
      }
    });
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
    setAddingProductId(null);
    setCurrentImageIndex(0);
  };

  const handleAddToCart = (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const selectedSize = selectedDialogSize || (product.availableSizes?.[0] || 'UNIQUE');

    if (isInCart(product.id, selectedSize)) {
      showAlreadyInCartToast();
      return;
    }

    if (addingProductId === product.id) return;
    
    setAddingProductId(product.id);

    // The product object from selectedProduct already has sale info
    const cartPayload = {
      ...product,
      size: selectedSize,
      source: 'From Collections Popup',
    };

    try {
        addToCart(cartPayload);
        showSuccessToast();

        // Use a timeout to give visual feedback and reset the button
        setTimeout(() => {
            setAddingProductId(null);
        }, 1500);
    } catch (error) {
        console.error('Erreur lors de l\'ajout au panier:', error);
        toast.error("❌ Erreur lors de l'ajout au panier");
        setAddingProductId(null); // Reset on error too
    }
  };


  // Effect to fetch filter data (categories and subcategories)
  useEffect(() => {
    const fetchFilterData = async () => {
      setLoadingFilters(true);
      try {
        const [categoriesResponse, subcategoriesResponse] = await Promise.all([
          categoriesApi.list(),
          subcategoriesApi.list()
        ]);
        
        // S'assurer que les catégories et sous-catégories sont des tableaux
        const categories = Array.isArray(categoriesResponse) ? categoriesResponse : [];
        const subcategories = Array.isArray(subcategoriesResponse) ? subcategoriesResponse : [];
        
        // Normaliser les IDs en nombres
        const normalizeId = (id) => {
          if (id === null || id === undefined) return null;
          const num = Number(id);
          return isNaN(num) ? id : num;
        };
        
        const structuredData = categories.map(cat => {
          const categoryId = normalizeId(cat.id);
          return {
            ...cat,
            id: categoryId,
            type: 'category',
            subcategories: subcategories
              .filter(sub => 
                Array.isArray(sub.categories) && 
                sub.categories.some(subCat => normalizeId(subCat.id) === categoryId)
              )
              .map(sub => ({ 
                ...sub, 
                id: normalizeId(sub.id),
                type: 'subcategory' 
              }))
          };
        });
        
        console.log('=== DONNÉES DE FILTRE ===');
        console.log('Structured Data:', JSON.stringify(structuredData, null, 2));
        console.log('Première catégorie:', structuredData[0] ? {
          id: structuredData[0].id,
          name: structuredData[0].name,
          subcategories: structuredData[0].subcategories.map(s => ({
            id: s.id,
            name: s.name,
            categories: s.categories
          }))
        } : 'Aucune catégorie trouvée');
        
        setFilterData(structuredData);
      } catch (err) {
        console.error("Failed to load filter data:", err);
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchFilterData();
  }, []);

  // Effect to fetch all products once
  useEffect(() => {
    const fetchAllProducts = async () => {
      setLoadingProducts(true);
      try {
        // Fetch all products - assuming the API can handle a large limit
        const data = await productsApi.list(1, 1000);
        console.log('Produits chargés depuis l\'API:', data.items);
        
        if (data.items && Array.isArray(data.items)) {
          // Normaliser la structure des données des produits
          const normalizedProducts = data.items.map(product => {
            // Fonction pour normaliser les IDs
            const normalizeId = (id) => {
              if (id === null || id === undefined || id === '') return null;
              if (typeof id === 'object' && id !== null) {
                if ('id' in id) return normalizeId(id.id);
                return null;
              }
              const num = Number(id);
              return isNaN(num) ? id : num;
            };
            
            // Extraire les IDs de catégorie et sous-catégorie des tableaux
            const categoryIds = (product.categories || []).map(cat => 
              normalizeId(cat?.id || cat)
            ).filter(Boolean);
            
            const subcategoryIds = (product.subCategories || []).map(subCat => 
              normalizeId(subCat?.id || subCat)
            ).filter(Boolean);
            
            // Prendre le premier ID de catégorie et sous-catégorie pour la rétrocompatibilité
            const primaryCategoryId = categoryIds[0] || null;
            const primarySubcategoryId = subcategoryIds[0] || null;
            
            // Créer les objets category et subcategory pour la rétrocompatibilité
            const primaryCategory = primaryCategoryId ? {
              id: primaryCategoryId,
              name: (product.categories?.[0]?.name) || 'Catégorie inconnue'
            } : null;
            
            const primarySubcategory = primarySubcategoryId ? {
              id: primarySubcategoryId,
              name: (product.subCategories?.[0]?.name) || 'Sous-catégorie inconnue',
              category_id: primaryCategoryId
            } : null;
            
            return {
              ...product,
              // Pour la rétrocompatibilité
              category: primaryCategory,
              subcategory: primarySubcategory,
              categoryId: primaryCategoryId,
              subcategoryId: primarySubcategoryId,
              // Nouvelles propriétés pour gérer les tableaux
              categoryIds,
              subcategoryIds,
              price: parseFloat(product.price) || 0,
            };
          });
          
          console.log('Structure du premier produit:', JSON.stringify(normalizedProducts[0], null, 2));
    console.log('Produits normalisés:', normalizedProducts); // Debug
          
          setAllProducts(normalizedProducts);
          const max = normalizedProducts.reduce((maxVal, p) => {
            return p.price > maxVal ? p.price : maxVal;
          }, 0);
          
          if (max > 0) {
            setPriceRange({ min: 0, max: Math.ceil(max) });
          }
        }
      } catch (err) {
        console.error("Failed to fetch all products:", err);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchAllProducts();
  }, []);

  // Fonction pour normaliser les IDs (déplacée en haut pour être accessible partout)
  const normalizeId = (id) => {
    if (id === null || id === undefined || id === '') return null;
    if (typeof id === 'object' && id !== null && 'id' in id) {
      return normalizeId(id.id);
    }
    const num = Number(id);
    return isNaN(num) ? id : num;
  };

  // Effect to apply filters and update filtered products
  useEffect(() => {
    // Ne pas filtrer tant que les produits ne sont pas chargés
    if (allProducts.length === 0) {
      console.log('Aucun produit à filtrer, attente du chargement...');
      setFilteredProducts([]);
      return;
    }
    
    // Vérifier la structure des produits
    console.log('Structure du premier produit:', allProducts[0] ? {
      id: allProducts[0].id,
      name: allProducts[0].name,
      price: allProducts[0].price,
      category: allProducts[0].category,
      subcategory: allProducts[0].subcategory,
      categoryId: allProducts[0].categoryId,
      subcategoryId: allProducts[0].subcategoryId
    } : 'Aucun produit chargé');
    
    console.log('=== DÉBUT DU FILTRAGE ===');
    console.log('Filtres actifs:', activeFilters);
    console.log('Plage de prix:', priceRange);
    console.log('Nombre total de produits:', allProducts.length);
    
    let productsToFilter = [...allProducts];
    console.log('Produits avant filtrage:', productsToFilter);
    
    // Afficher les catégories et sous-catégories disponibles dans les produits
    const allCategories = [];
    const allSubcategories = [];
    
    productsToFilter.forEach(p => {
      // Détection des catégories
      if (p.category) {
        const catId = typeof p.category === 'object' ? p.category.id : p.category;
        const normalizedId = normalizeId(catId);
        if (normalizedId !== null && !allCategories.includes(normalizedId)) {
          allCategories.push(normalizedId);
        }
      } else if (p.categoryId) {
        const normalizedId = normalizeId(p.categoryId);
        if (normalizedId !== null && !allCategories.includes(normalizedId)) {
          allCategories.push(normalizedId);
        }
      }
      
      // Détection des sous-catégories
      if (p.subcategory) {
        const subId = typeof p.subcategory === 'object' ? p.subcategory.id : p.subcategory;
        const normalizedId = normalizeId(subId);
        if (normalizedId !== null && !allSubcategories.includes(normalizedId)) {
          allSubcategories.push(normalizedId);
        }
      } else if (p.subcategoryId) {
        const normalizedId = normalizeId(p.subcategoryId);
        if (normalizedId !== null && !allSubcategories.includes(normalizedId)) {
          allSubcategories.push(normalizedId);
        }
      }
    });
    
    console.log('Catégories disponibles dans les produits:', allCategories);
    console.log('Sous-catégories disponibles dans les produits:', allSubcategories);


    // Category and Subcategory filtering
    if (activeFilters.categories && activeFilters.categories.length > 0) {
        const categoryIds = activeFilters.categories.map(normalizeId);
        console.log('Filtrage par catégories:', categoryIds);
        
        productsToFilter = productsToFilter.filter(p => {
          // Utiliser le tableau categoryIds si disponible, sinon essayer les anciennes méthodes
          const productCategoryIds = Array.isArray(p.categoryIds) && p.categoryIds.length > 0 
            ? p.categoryIds 
            : [
                p.categoryId,
                p.category?.id,
                p.category_id,
                p.category
              ].filter(Boolean).map(normalizeId);
          
          const isIncluded = productCategoryIds.some(id => 
            id !== null && categoryIds.includes(id)
          );
          
          if (isIncluded) {
            console.log('Produit inclus par catégorie:', {
              id: p.id,
              name: p.name,
              productCategoryIds,
              wantedCategories: categoryIds
            });
          } else {
            console.log('Produit EXCLU par catégorie:', {
              id: p.id,
              name: p.name,
              productCategoryIds,
              wantedCategories: categoryIds
            });
          }
          
          return isIncluded;
        });
        
        console.log('Après filtrage par catégorie:', productsToFilter.length, 'produits');
    }
    
    if (activeFilters.subcategories && activeFilters.subcategories.length > 0) {
        const subcategoryIds = activeFilters.subcategories.map(normalizeId);
        console.log('Filtrage par sous-catégories:', subcategoryIds);
        
        productsToFilter = productsToFilter.filter(p => {
          // Utiliser le tableau subcategoryIds si disponible, sinon essayer les anciennes méthodes
          const productSubcategoryIds = Array.isArray(p.subcategoryIds) && p.subcategoryIds.length > 0 
            ? p.subcategoryIds 
            : [
                p.subcategoryId,
                p.subcategory?.id,
                p.subcategory_id,
                p.subcategory
              ].filter(Boolean).map(normalizeId);
          
          const isIncluded = productSubcategoryIds.some(id => 
            id !== null && subcategoryIds.includes(id)
          );
          
          if (isIncluded) {
            console.log('Produit inclus par sous-catégorie:', {
              id: p.id,
              name: p.name,
              productSubcategoryIds,
              wantedSubcategories: subcategoryIds
            });
          } else {
            console.log('Produit EXCLU par sous-catégorie:', {
              id: p.id,
              name: p.name,
              productSubcategoryIds,
              wantedSubcategories: subcategoryIds
            });
          }
          
          return isIncluded;
        });
        
        console.log('Après filtrage par sous-catégorie:', productsToFilter.length, 'produits');
    }

    // Price filtering
    productsToFilter = productsToFilter.filter(p => {
      const price = parseFloat(p.price);
      const isInPriceRange = price >= debouncedPriceRange.min && price <= debouncedPriceRange.max;
      
      if (!isInPriceRange) {
        console.log('Produit exclu par prix:', {
          id: p.id,
          name: p.name,
          price: price,
          min: debouncedPriceRange.min,
          max: debouncedPriceRange.max
        });
      }
      
      return isInPriceRange;
    });

    console.log('=== FIN DU FILTRAGE ===');
    console.log('Produits après tous les filtres:', productsToFilter);
    
    setFilteredProducts(productsToFilter);
    setCurrentPage(1); // Reset to first page after filtering

  }, [allProducts, activeFilters, debouncedPriceRange]);

  // Effect for client-side pagination
  useEffect(() => {
    const newTotalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
    setTotalPages(newTotalPages > 0 ? newTotalPages : 1);

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setPaginatedProducts(filteredProducts.slice(startIndex, endIndex));

  }, [filteredProducts, currentPage]);


  const handleCategoryToggle = (categoryId, isChecked) => {
    // Normaliser l'ID de la catégorie
    const normalizeId = (id) => {
      if (id === null || id === undefined) return null;
      const num = Number(id);
      return isNaN(num) ? id : num;
    };
    
    const normalizedCategoryId = normalizeId(categoryId);
    const category = filterData.find(c => normalizeId(c.id) === normalizedCategoryId);
    
    if (!category) {
      console.warn('Catégorie non trouvée:', categoryId);
      return;
    }

    const subIdsForCategory = Array.isArray(category.subcategories) 
      ? category.subcategories.map(sub => normalizeId(sub.id)).filter(Boolean)
      : [];
    
    setActiveFilters(prevFilters => {
      // Normaliser les IDs existants
      const currentCategories = (prevFilters.categories || []).map(normalizeId);
      const currentSubcategories = (prevFilters.subcategories || []).map(normalizeId);
      
      let newCategories = [...currentCategories];
      let newSubcategories = [...currentSubcategories];

      if (isChecked) {
        // Ajouter la catégorie si elle n'est pas déjà présente
        if (!newCategories.includes(normalizedCategoryId)) {
          newCategories.push(normalizedCategoryId);
        }
        // Ajouter toutes les sous-catégories de cette catégorie
        subIdsForCategory.forEach(subId => {
          if (!newSubcategories.includes(subId)) {
            newSubcategories.push(subId);
          }
        });
      } else {
        // Retirer la catégorie
        newCategories = newCategories.filter(id => id !== normalizedCategoryId);
        // Retirer toutes les sous-catégories de cette catégorie
        newSubcategories = newSubcategories.filter(subId => !subIdsForCategory.includes(subId));
      }

      console.log('Nouveaux filtres:', { 
        categories: newCategories, 
        subcategories: newSubcategories 
      });
      
      return { 
        categories: [...new Set(newCategories)], 
        subcategories: [...new Set(newSubcategories)] 
      };
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubcategoryChange = (subcategoryId, isChecked, parentCategoryId) => {
    // Normaliser les IDs
    const normalizeId = (id) => {
      if (id === null || id === undefined) return null;
      const num = Number(id);
      return isNaN(num) ? id : num;
    };
    
    const normalizedSubcategoryId = normalizeId(subcategoryId);
    const normalizedParentId = normalizeId(parentCategoryId);
    
    setActiveFilters(prevFilters => {
      // Normaliser les IDs existants
      const currentSubcategories = (prevFilters.subcategories || []).map(normalizeId);
      const currentCategories = (prevFilters.categories || []).map(normalizeId);
      
      let newSubcategories = [...currentSubcategories];
      let newCategories = [...currentCategories];

      if (isChecked) {
        // Ajouter la sous-catégorie si elle n'est pas déjà présente
        if (!newSubcategories.includes(normalizedSubcategoryId)) {
          newSubcategories.push(normalizedSubcategoryId);
        }
      } else {
        // Retirer la sous-catégorie
        newSubcategories = newSubcategories.filter(id => id !== normalizedSubcategoryId);
      }
      
      // Vérifier si la catégorie parente doit être mise à jour
      if (normalizedParentId) {
        const category = filterData.find(c => normalizeId(c.id) === normalizedParentId);
        if (category && Array.isArray(category.subcategories)) {
          const subIdsForCategory = category.subcategories
            .map(sub => normalizeId(sub.id))
            .filter(Boolean);
            
          const allSubcategoriesChecked = subIdsForCategory.every(id => 
            newSubcategories.includes(id)
          );
          
          if (allSubcategoriesChecked) {
            // Toutes les sous-catégories sont cochées, cocher la catégorie parente
            if (!newCategories.includes(normalizedParentId)) {
              newCategories.push(normalizedParentId);
            }
          } else {
            // Décocher la catégorie parente si nécessaire
            newCategories = newCategories.filter(id => id !== normalizedParentId);
          }
        }
      }

      // Check if parent category needs to be updated
      // La variable newCategories est déjà déclarée plus haut dans la fonction
      const parentCategory = filterData.find(c => c.id === parentCategoryId);

      if (parentCategory) {
        const siblingSubcategoryIds = parentCategory.subcategories.map(s => s.id);
        const allSiblingsChecked = siblingSubcategoryIds.every(id => newSubcategories.includes(id));

        if (allSiblingsChecked) {
          if (!newCategories.includes(parentCategoryId)) {
            newCategories = [...newCategories, parentCategoryId];
          }
        } else {
          if (newCategories.includes(parentCategoryId)) {
            newCategories = newCategories.filter(id => id !== parentCategoryId);
          }
        }
      }

      return { categories: newCategories, subcategories: newSubcategories };
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetFilters = () => {
    setActiveFilters({ categories: [], subcategories: [] });
    setPriceRange({ min: 0, max: MAX_PRICE });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        <UrgencyBanner />
        {/* Hero Section */}
        <section className={`relative py-20 lg:py-28 overflow-hidden ${
          isDark 
            ? 'bg-gradient-to-r from-background via-surface to-background' 
            : 'bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800'
        }`}>
          {/* Pattern Overlay */}
          <div className={`absolute inset-0 opacity-10 ${
            isDark 
              ? "bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2300FF88%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"
              : "bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23FFFFFF%22%20fill-opacity%3D%221%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"
          } `} />
          
          {/* Gradient Overlays */}
          {!isDark && (
            <>
              <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/30 rounded-full filter blur-3xl" />
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/30 rounded-full filter blur-3xl" />
            </>
          )}
          
          <div className="relative max-w-7xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <h1 className={`font-heading font-bold text-5xl lg:text-7xl mb-6 ${
                isDark ? 'text-foreground' : 'text-white'
              }`}>
                Urban
                <span className={`${
                  isDark ? 'text-accent text-shadow-glow' : 'text-cyan-300'
                }`}> Space</span>
                <br />
                Collections
              </h1>
              <p className={`text-lg lg:text-xl max-w-2xl mx-auto ${
                isDark ? 'text-muted-foreground' : 'text-blue-100'
              }`}>
                Discover exclusive streetwear pieces from the most coveted brands in urban fashion culture
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filters and Products Section */}
        <section className="px-4 lg:px-6 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filter Panel */}
            <aside className="w-full lg:w-1/4 xl:w-1/5">
              {loadingFilters ? (
                <div className="text-center text-muted-foreground">Loading filters...</div>
              ) : (
                <CombinedFilter
                  filterData={filterData}
                  activeFilters={activeFilters}
                  onCategoryToggle={handleCategoryToggle}
                  onSubcategoryChange={handleSubcategoryChange}
                  onReset={handleResetFilters}
                  priceRange={priceRange}
                  onPriceChange={setPriceRange}
                  maxPrice={MAX_PRICE}
                />
              )}
            </aside>

            {/* Products Grid */}
            <div className="w-full lg:w-3/4 xl:w-4/5">
              {totalPages > 1 && (
                <div className="flex justify-end mb-4">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}

              {loadingProducts ? (
                <div className="text-center text-muted-foreground mt-10">Loading products...</div>
              ) : paginatedProducts.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {paginatedProducts.map(product => (
                    <ProductCard
                      key={product.id}
                      product={{
                        ...product,
                        images: [product.urlImage, product.urlImageHover].filter(Boolean),
                        isNew: new Date(product.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                      }}
                      onProductClick={handleProductClick}
                      onAddToCart={prod => console.log("Add to cart", prod)}
                      onToggleWishlist={id => console.log("Toggle wishlist", id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground mt-10">
                  No products found for this selection.
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex justify-center mt-10">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
        
        <SaleHero />
        
        {/* Newsletter Section */}
        <section className="bg-surface py-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-foreground mb-4">
                Restez informé
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Obtenez un accès exclusif aux nouvelles sorties, aux sorties limitées et aux informations sur la culture de la rue
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-background border border-street rounded-lg focus:border-accent focus:outline-none text-foreground"
                />
                <Button variant="default" className="px-8">
                  S'abonner
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Product Detail Dialog */}
      {selectedProduct && (
        <Dialog
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          title={selectedProduct.title}
          showCancel={false}
          showConfirm={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Enhanced Product Image Slider */}
            <div className="relative group aspect-[3/4] overflow-hidden rounded-lg md:rounded-lg w-full md:w-full mx-auto">
              {/* Main Image with smooth transition */}
              <motion.div
                key={currentImageIndex}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full"
              >
                <Image
                  src={images.length > 0 ? images[currentImageIndex] : selectedProduct.image}
                  alt={selectedProduct.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              
              {images.length > 1 && (
                <>
                  {/* Navigation Arrows */}
                  <div className="absolute inset-0 flex items-center justify-between p-2">
                    {/* Previous Button */}
                    <motion.button
                      onClick={handlePrevImage}
                      className="h-10 w-10 flex items-center justify-center bg-white/90 text-gray-900 rounded-full shadow-lg hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-all duration-300 z-10"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Image précédente"
                    >
                      <Icon name="ChevronLeft" size={24} className="text-gray-800" />
                    </motion.button>
                    
                    {/* Next Button */}
                    <motion.button
                      onClick={handleNextImage}
                      className="h-10 w-10 flex items-center justify-center bg-white/90 text-gray-900 rounded-full shadow-lg hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 transition-all duration-300 z-10"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label="Image suivante"
                    >
                      <Icon name="ChevronRight" size={24} className="text-gray-800" />
                    </motion.button>
                  </div>

                  {/* Image indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(index);
                        }}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          currentImageIndex === index 
                            ? 'bg-white w-6 scale-125' 
                            : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`Aller à l'image ${index + 1}`}
                      />
                    ))}
                  </div>

                  {/* Image counter */}
                  <div className="absolute top-4 right-4 bg-black/60 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Product Details */}
            <div className="flex flex-col p-4 md:p-0">
              <div className="flex-grow">
                <p className="text-sm text-muted-foreground mb-1">{selectedProduct.category}</p>
                <h3 className="text-2xl font-bold text-foreground mb-3">{selectedProduct.title}</h3>
                
                <p className="text-md text-foreground mb-4">{selectedProduct.description}</p>

                <div className="flex items-baseline mb-4">
                  <p className="text-3xl text-accent font-extrabold">
                    {selectedProduct.price.toFixed(3)} TND
                  </p>
                  {selectedProduct.originalPrice && (
                    <span className="ml-3 text-lg text-muted-foreground line-through">
                      {selectedProduct.originalPrice.toFixed(3)} TND
                    </span>
                  )}
                </div>

                {selectedProduct.inStock ? (
                  <p className="text-md font-semibold text-success mb-4">
                    <Icon name="CheckCircle" className="inline-block mr-2" />
                    En stock ({selectedProduct.stockCount} restants)
                  </p>
                ) : (
                  <p className="text-md font-semibold text-error mb-4">
                    <Icon name="XCircle" className="inline-block mr-2" />
                    Rupture de stock
                  </p>
                )}
              </div>

              {/* Sélection de la taille dans la boîte de dialogue */}
              {selectedProduct.availableSizes?.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-foreground mb-3">TAILLE</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.availableSizes.map((size) => (
                      <button
                        key={size}
                        type="button"
                        className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                          selectedDialogSize === size
                            ? 'bg-accent text-white border-accent'
                            : 'bg-background text-foreground border-gray-200 hover:border-accent/50'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDialogSize(size);
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <Button 
                  variant={isInCart(selectedProduct.id, selectedDialogSize) ? 'ghost' : 'solid'}
                  size="lg"
                  className={`w-full transition-all py-4 ${
                    isInCart(selectedProduct.id, selectedDialogSize) 
                      ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-default' 
                      : `bg-accent text-white hover:bg-accent/90 ${
                          addingProductId === selectedProduct.id ? 'opacity-75 cursor-not-allowed' : ''
                        }`
                  }`}
                  onClick={(e) => handleAddToCart(selectedProduct, e)}
                  disabled={!selectedProduct.inStock || 
                           addingProductId === selectedProduct.id || 
                           isInCart(selectedProduct.id, selectedDialogSize)}
                >
                  {addingProductId === selectedProduct.id ? (
                    <span className="flex items-center justify-center text-lg">
                      <Icon name="Loader" className="h-6 w-6 animate-spin mr-3" />
                      Ajout...
                    </span>
                  ) : isInCart(selectedProduct.id, selectedDialogSize) ? (
                    <span className="flex items-center justify-center text-lg">
                    
                      Déjà au panier
                    </span>
                  ) : (
                    <span className="flex items-center justify-center text-lg">
                      <Icon name="ShoppingBag" className="mr-3 h-6 w-6" />
                      Ajouter au panier
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};


export default Collections;